import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { buildSupabaseFilters } from '../utils/roleFilters';
import { CONNECTED_OUTCOMES } from '../utils/callOutcomes';

interface DashboardCounts {
  callsDone: number;
  whatsappsSent: number;
  emailsSent: number;
  linkedInMessagesSent: number;
  connectedCalls: number;
  whatsappReplies: number;
  emailReplies: number;
  linkedInReplies: number;
  isLoading: boolean;
  recentCallLogs: any[];
}

export function useDashboardCounts(): DashboardCounts {
  const [counts, setCounts] = useState({
    callsDone: 0,
    whatsappsSent: 0,
    emailsSent: 0,
    linkedInMessagesSent: 0,
    connectedCalls: 0,
    whatsappReplies: 0,
    emailReplies: 0,
    linkedInReplies: 0,
  });
  const [recentCallLogs, setRecentCallLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const isMountedRef = useRef(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchCounts = useCallback(async () => {
    try {
      const filters = buildSupabaseFilters();
      const clientFilter = filters.find((f: any) => f.column === 'client_id');
      const userFilter = filters.find((f: any) => f.column === 'user_id');

      const applyFilters = (query: any) => {
        if (clientFilter) query = query.eq('client_id', clientFilter.value);
        if (userFilter) query = query.eq('user_id', userFilter.value);
        return query;
      };

      const connectedOutcomesArray = Array.from(CONNECTED_OUTCOMES);

      const [callCount, waCount, emailCount, liCount, connectedCount, waReplyCount, emailReplyCount, liReplyCount, callRecent] = await Promise.all([
        applyFilters(
          supabase.from('call_logs').select('*', { count: 'exact', head: true })
        ),
        applyFilters(
          supabase.from('whatsapp_logs').select('*', { count: 'exact', head: true }).eq('from_role', 'user')
        ),
        applyFilters(
          supabase.from('email_logs').select('*', { count: 'exact', head: true }).eq('from_role', 'user')
        ),
        applyFilters(
          supabase.from('linkedin_logs').select('*', { count: 'exact', head: true }).eq('from_role', 'user')
        ),
        applyFilters(
          supabase.from('call_logs').select('*', { count: 'exact', head: true }).in('call_outcome', connectedOutcomesArray)
        ),
        applyFilters(
          supabase.from('whatsapp_logs').select('*', { count: 'exact', head: true }).eq('from_role', 'lead')
        ),
        applyFilters(
          supabase.from('email_logs').select('*', { count: 'exact', head: true }).eq('from_role', 'prospect')
        ),
        applyFilters(
          supabase.from('linkedin_logs').select('*', { count: 'exact', head: true }).eq('from_role', 'prospect')
        ),
        applyFilters(
          supabase.from('call_logs')
            .select('call_id_internal, lead_id, call_outcome, created_at')
            .in('call_outcome', ['Info Requested', 'Call Later', 'Interested', 'Meeting Booked'])
            .order('created_at', { ascending: false })
            .limit(5)
        ),
      ]);

      if (!isMountedRef.current) return;

      setCounts({
        callsDone: callCount.count ?? 0,
        whatsappsSent: waCount.count ?? 0,
        emailsSent: emailCount.count ?? 0,
        linkedInMessagesSent: liCount.count ?? 0,
        connectedCalls: connectedCount.count ?? 0,
        whatsappReplies: waReplyCount.count ?? 0,
        emailReplies: emailReplyCount.count ?? 0,
        linkedInReplies: liReplyCount.count ?? 0,
      });
      setRecentCallLogs(callRecent.data ?? []);
      setIsLoading(false);
    } catch (err) {
      console.error('[useDashboardCounts] Error:', err);
      if (isMountedRef.current) setIsLoading(false);
    }
  }, []);

  const debouncedFetch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCounts, 500);
  }, [fetchCounts]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchCounts();

    const channel = supabase
      .channel('dashboard_counts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'call_logs' }, debouncedFetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_logs' }, debouncedFetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'email_logs' }, debouncedFetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'linkedin_logs' }, debouncedFetch)
      .subscribe();

    return () => {
      isMountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [fetchCounts, debouncedFetch]);

  return { ...counts, isLoading, recentCallLogs };
}
